// ... existing code ...
import { Tabs as TabsPrimitive } from "radix-ui"
import * as React from "react"

import { cn } from "@/lib/utils"

// 1. Create a context for the variant
const TabsVariantContext = React.createContext<"line" | "outline">("line");

function Tabs({
  className,
  variant = "line", // 2. Add variant prop
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Root> & { variant?: "line" | "outline" }) {
  return (
    <TabsVariantContext.Provider value={variant}>
      <TabsPrimitive.Root
        data-slot="tabs"
        className={cn("flex flex-col gap-2", className)}
        {...props}
      />
    </TabsVariantContext.Provider>
  )
}

function TabsList({
  className,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.List>) {
  // 3. Get variant from context
  const variant = React.useContext(TabsVariantContext);
  return (
    <TabsPrimitive.List
      data-slot="tabs-list"
      className={cn(
        "inline-flex h-8 w-fit items-center justify-center gap-0.5 p-0.5",
        variant === "outline"
          ? "h-auto w-full justify-start gap-5 rounded-none border-b bg-transparent p-0"
          : "bg-muted rounded-full",
        className
      )}
      {...props}
    />
  )
}

function TabsTrigger({
  className,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Trigger>) {
  const variant = React.useContext(TabsVariantContext);
  return (
    <TabsPrimitive.Trigger
      data-slot="tabs-trigger"
      className={cn(
        // Common styles
        "text-muted-foreground inline-flex flex-1 items-center justify-center gap-1.5 rounded-full px-3 py-1 text-[13px] font-medium data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:ring-1 data-[state=active]:ring-border whitespace-nowrap transition-[color,box-shadow] focus-visible:ring-[3px] focus-visible:outline-1 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        // Variant styles
        variant === "line" &&
          "border-b border-transparent data-[state=active]:ring-0 data-[state=active]:border-b-2 rounded-none data-[state=active]:border-foreground data-[state=active]:bg-transparent data-[state=active]:text-primary",
        variant === "outline" &&
          "-mb-px flex-none rounded-none border-b-2 border-transparent px-0 pb-2.5 pt-0 text-sm font-normal hover:text-foreground data-[state=active]:border-foreground data-[state=active]:bg-transparent data-[state=active]:font-medium data-[state=active]:text-foreground data-[state=active]:ring-0 focus-visible:ring-0 focus-visible:outline-none focus-visible:text-foreground",
        className
      )}
      {...props}
    />
  )
}

function TabsContent({
  className,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Content>) {
  return (
    <TabsPrimitive.Content
      data-slot="tabs-content"
      className={cn("flex-1 outline-none", className)}
      {...props}
    />
  )
}

export { Tabs, TabsContent, TabsList, TabsTrigger }